import { motion } from 'framer-motion'
import { ReactNode, useMemo } from 'react'

import { useDesignPixel } from '@shared'

import { DoneIcon } from './DoneIcon'
import styles from './GlowingCircleWrapper.module.scss'

type GlowingCircleWrapperProps = {
  children: ReactNode
  size: {
    width: string
    height: string
  }
  isCompleted?: boolean
}

function parseRemToPx(rem: string, designPixelValue: number): number {
  const remValue = parseFloat(rem)
  return remValue * designPixelValue
}

const circleAnimations = {
  initial: { opacity: 0, scale: 0.9 },
  animate: (i: number) => ({
    opacity: [0, 1, 0],
    scale: [0.9, 1.1, 1],
    transition: {
      duration: 2.4,
      repeat: Infinity,
      delay: i * 0.4,
    },
  }),
}

export const GlowingCircleWrapper = ({
  children,
  size,
  isCompleted = false,
}: GlowingCircleWrapperProps) => {
  const { designPixelValue } = useDesignPixel()

  const pixelSize = useMemo(() => {
    const widthPx = parseRemToPx(size.width, designPixelValue) * 1.2
    const heightPx = parseRemToPx(size.height, designPixelValue) * 1.2
    const max = Math.max(widthPx, heightPx)

    return max * 2
  }, [size, designPixelValue])

  const cx = pixelSize / 2
  const cy = pixelSize / 2

  const radii = [pixelSize * 0.4, pixelSize * 0.3, pixelSize * 0.2]

  return (
    <div className={styles.glowingCircleWrapper}>
      <svg
        width={pixelSize}
        height={pixelSize}
        viewBox={`0 0 ${pixelSize} ${pixelSize}`}
        className={styles.svgWrapper}
      >
        <circle cx={cx} cy={cy} r={radii[radii.length - 1]} fill="white" />
      </svg>
      {radii.map((r, i) => (
        <motion.svg
          key={i}
          width={pixelSize}
          height={pixelSize}
          viewBox={`0 0 ${pixelSize} ${pixelSize}`}
          className={styles.svgWrapper}
          initial="initial"
          animate="animate"
          custom={i}
          variants={circleAnimations}
        >
          <g opacity="0.2" filter={`url(#glow${i})`}>
            <circle cx={cx} cy={cy} r={r} fill="white" />
          </g>
          <defs>
            <filter
              id={`glow${i}`}
              x="0"
              y="0"
              width={pixelSize}
              height={pixelSize}
              filterUnits="userSpaceOnUse"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset />
              <feGaussianBlur stdDeviation={pixelSize * 0.05} />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow"
                result="shape"
              />
            </filter>
          </defs>
        </motion.svg>
      ))}

      <div className={styles.contentWrapper}>
        {isCompleted ? <DoneIcon /> : children}
      </div>
    </div>
  )
}
